import React, { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../provider/authProvider";
import { selectUser } from "../../features/user/userSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { revalidateToken } from "../../features/user/utils";
import { AvvisoSolaLettura } from "../../components/avviso-sola-lettura";

type Verifica = "in-corso" | "valida" | "non-valida";

/*
 La pagina protetta si mostra solo quando si sa che la sessione e' buona.

 Prima la verifica partiva dentro il render e il `<Navigate>` veniva restituito dal `.then` di
 una promessa, dove React non lo vede: la rotta mostrava comunque `<Outlet />`, e con un token
 morto la pagina si apriva e ogni sua chiamata prendeva 401. Ora l'esito e' uno stato: finche'
 la verifica e' in corso non si mostra niente, e se non risponde 200 si va al login.

 La verifica serve solo quando l'utente non e' ancora in Redux, cioe' al primo caricamento con
 il token letto dal cookie. Con l'utente gia' presente la sessione si da' per buona: se il token
 nel frattempo e' scaduto lo dira' il primo 401, e l'interceptor di `authProvider` chiude la
 sessione.

 Al login si torna solo se il server risponde 401. Se non risponde affatto - rete giu', un
 deploy in corso - o risponde un altro errore, il token puo' essere ancora buono: buttarlo
 costringerebbe a rimettere la password per un problema che non e' la sessione. La pagina si
 apre come prima, le sue chiamate falliscono per conto loro, e la verifica si ritenta a ogni
 cambio di pagina finche' l'utente non e' caricato: senza, nome e ruolo resterebbero vuoti, e
 con loro le voci di menu che dipendono dal ruolo.
*/
export const ProtectedRoute = () => {
  const { token, setToken } = useAuth();
  const dispatch = useAppDispatch();
  const userStore = useAppSelector(selectUser);
  const { pathname } = useLocation();
  const daVerificare = !!token && userStore.firstName.length === 0;
  const [verifica, setVerifica] = useState<Verifica>(
    daVerificare ? "in-corso" : "valida"
  );
  // setToken cambia identita' a ogni render di AuthProvider, e la revalidate stessa lo fa
  // rirenderizzare: fra le dipendenze dell'effect rilancerebbe la verifica a ogni giro.
  const setTokenRef = useRef(setToken);
  setTokenRef.current = setToken;

  useEffect(() => {
    if (!daVerificare) {
      return;
    }
    let annullata = false;
    // Un nuovo tentativo, a pagina gia' aperta, non la svuota.
    setVerifica((attuale) => (attuale === "valida" ? attuale : "in-corso"));
    revalidateToken(setTokenRef.current, dispatch).then((status) => {
      if (annullata) {
        return;
      }
      if (status === 401) {
        // Il token non vale piu': lo si butta, cosi' anche il cookie se ne va e il login non
        // rimanda di nuovo qui.
        setTokenRef.current("logout");
        setVerifica("non-valida");
      } else {
        setVerifica("valida");
      }
    });
    return () => {
      annullata = true;
    };
  }, [daVerificare, dispatch, pathname]);

  if (!token || verifica === "non-valida") {
    return <Navigate to="/login" />;
  }
  // Con l'utente gia' caricato non c'e' niente da aspettare, anche se l'effect e' stato annullato:
  // la revalidate riuscita mette l'utente in Redux prima di restituire, e il rerender che ne
  // segue puo' annullare l'effect prima che legga il suo 200.
  if (verifica === "in-corso" && daVerificare) {
    return null;
  }
  // L'avviso di sola lettura (ODOO 300) sta qui perché riguarda ogni pagina protetta.
  return (
    <>
      <AvvisoSolaLettura />
      <Outlet />
    </>
  );
};
